/**
 * Reporting lines (employee to employee, effective dated). Mirrors the backend
 * `reporting-lines` module (`companies/:companyId/employees/:employeeId/
 * reporting-line` and `companies/:companyId/reporting-lines/*`).
 */

/** Stable error codes the backend returns as `errorCode`. */
export enum ReportingErrorCode {
  Cycle = 'reporting.cycle',
  SelfManager = 'reporting.selfManager',
  ManagerIneligible = 'reporting.managerIneligible',
  EmployeeIneligible = 'reporting.employeeIneligible',
  EmployeeNotFound = 'reporting.employeeNotFound',
  ConcurrentChange = 'reporting.concurrentChange',
  /** A temporary group changed since HR loaded it (409, D54). */
  GroupChanged = 'reporting.groupChanged',
  /** Assigning a temporary group to the person already covering it. */
  GroupSameManager = 'reporting.groupSameManager',
}

export type ReportingLineSource =
  | 'manual'
  | 'import'
  | 'handover'
  | 'migration_suggestion';

export type ReportingLineEndReason =
  | 'reassigned'
  | 'removed'
  | 'employee_departed'
  | 'manager_departed'
  | 'handover'
  | 'corrected'
  /** A temporary cover ended: the group moved to a new manager (3.4). */
  | 'interim_ended'
  /** A temporary cover was made permanent with the same manager (3.4). */
  | 'interim_confirmed';

/** Names, title and photo only: what HR screens show about a person. */
export interface ReportingPerson {
  employeeId: string;
  firstName: string;
  lastName: string;
  profileImage: string | null;
  designation: string | null;
  branchName: string | null;
  active: boolean;
}

export interface ReportingLineView {
  employeeId: string;
  manager: ReportingPerson | null;
  line: {
    id: string;
    effectiveFrom: string;
    isInterim: boolean;
    source: ReportingLineSource;
  } | null;
  directReportCount: number;
  /** Direct and indirect reports (the manager's full subtree). */
  teamSize: number;
}

export interface ReportingHistoryEntry {
  id: string;
  manager: ReportingPerson | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  isInterim: boolean;
  source: ReportingLineSource;
  endReason: ReportingLineEndReason | null;
  createdBy: { userId: string; name: string } | null;
  endedBy: { userId: string; name: string } | null;
}

export interface OrgChartNode extends ReportingPerson {
  managerEmployeeId: string | null;
  isInterim: boolean;
}

export interface ReportingSuggestion {
  employee: ReportingPerson;
  suggestedManager: ReportingPerson;
  valid: boolean;
  errorCode: ReportingErrorCode | null;
}

export interface SetReportingLinePayload {
  /** Null clears the manager. */
  managerEmployeeId: string | null;
  isInterim?: boolean;
}

export type BulkReportingSource = 'import' | 'suggestion';

export interface BulkReportingLinesPayload {
  items: {
    employeeId: string;
    managerEmployeeId: string | null;
    isInterim?: boolean;
  }[];
  source?: BulkReportingSource;
}

export interface BulkReportingRowResult {
  employeeId: string;
  status: 'applied' | 'unchanged' | 'error';
  errorCode?: ReportingErrorCode;
}

export interface BulkReportingLinesResult {
  applied: number;
  unchanged: number;
  failed: number;
  results: BulkReportingRowResult[];
}

/**
 * Temporary groups (sprint spec 3.4, D53): the open temporary lines sharing a
 * covering manager and a handover id, from
 * `GET companies/:companyId/reporting-lines/temporary-groups`. Not a table:
 * derived from the reporting lines every time.
 */
export interface TemporaryGroupPerson {
  employeeId: string;
  name: string;
}

export interface TemporaryGroupMember extends TemporaryGroupPerson {
  firstName: string;
  lastName: string;
  profileImage: string | null;
  jobTitle: string | null;
  branch: string | null;
  /** When this member's temporary line started. */
  since: string;
}

export interface TemporaryGroup {
  /** `<handoverId | 'manual'>:<coveringManagerEmployeeId>` */
  groupKey: string;
  handoverId: string | null;
  /** Whose departure created the group; null for manual groups (D55). */
  formerManager: TemporaryGroupPerson | null;
  coveringManager: TemporaryGroupPerson;
  /** Earliest start among the members' temporary lines. */
  since: string;
  members: TemporaryGroupMember[];
}

/** Which group, and which of its members, a move applies to. */
export interface TemporaryGroupRefPayload {
  handoverId: string | null;
  coveringManagerEmployeeId: string;
  employeeIds: string[];
}

export interface AssignTemporaryGroupPayload extends TemporaryGroupRefPayload {
  newManagerEmployeeId: string;
  /** Keep the group temporary under the new manager (default false). */
  temporary?: boolean;
}

export interface TemporaryGroupMoveResult {
  moved: number;
  handoverId: string | null;
  coveringManagerEmployeeId: string;
  managerEmployeeId: string;
  temporary: boolean;
  employeeIds: string[];
}

/** `errorDetails` of a 409 `reporting.groupChanged`: the members right now. */
export interface TemporaryGroupChangedDetails {
  groupKey: string;
  members: {
    employeeId: string;
    name: string;
    jobTitle: string | null;
    branch: string | null;
  }[];
}
